import { useState, useEffect, useRef } from "react";
import {
    X,
    Mail,
    Maximize2,
    Minimize2,
    MessageSquareIcon,
    LightbulbIcon,
    CheckSquareIcon,
    ClockIcon,
    UsersIcon,
    TrendingUpIcon,
    DollarSignIcon,
} from "lucide-react";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";
import { CandidateInterviewStatus, StartupCandidate } from "@type/candidate";
import { InterviewPlayer } from "@component/interview-player";
import { getInterviewInsights, getVideo } from "@service/interview";
import { InterviewInsight, InterviewVideo } from "@type/videoInterview";
import { getStatusBadgeClass } from "@util/string";
import { formatDuration, parseFormat } from "@util/date";

interface Props {
    candidate: StartupCandidate;
    isOpen: boolean;
    onClose: () => void;
}

export default function DrawerStartupCandidateProfile({
    candidate,
    isOpen,
    onClose,
}: Props) {
    const [activeTab, setActiveTab] = useState<string>("profile");
    const [activeSection, setActiveSection] = useState<string>("");
    const [isLoadingVideo, setLoadingVideo] = useState<boolean>(true);
    const [isLoadingInterviewInsight, setLoadingInterviewInsight] = useState<boolean>(true);
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [interviewInsights, setInterviewInsights] = useState<InterviewInsight | null>(null);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({
        personal: null,
        skills: null,
        experience: null,
        education: null,
        awards: null,
        certifications: null,
        projects: null,
    });

    useEffect(() => {
        setActiveTab("profile");
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        setVideoInterview(undefined);
    }, [candidate])

    // Reset scroll position when drawer opens or tab changes
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
        // Reset active section when tab changes
        if (activeTab === "profile") {
            setActiveSection("");
        }
    }, [isOpen, activeTab]);

    useEffect(() => {
        if (candidate && activeTab === "ai-interview") {
            getVideo(candidate.interview_submission_id).then((resp) => {
                if (resp) {
                    setVideoInterview(resp);
                }
                setLoadingVideo(false)
            }).catch((error) => {
                console.error("failed to get video interview", error);
                setLoadingVideo(false);
            });

            getInterviewInsights(candidate.interview_submission_id).then((res) => {
                if (res?.insights) {
                    console.log("interview insights", JSON.parse(res.insights));
                    setInterviewInsights(JSON.parse(res.insights))
                }
                setLoadingInterviewInsight(false);
            }).catch((error) => {
                console.error("failed to get interview insight", error);
                setLoadingInterviewInsight(false);
            })
        }
    }, [candidate, activeTab])

    // Scroll to section when activeSection changes
    useEffect(() => {
        if (
            activeTab === "profile" &&
            activeSection &&
            sectionRefs.current[activeSection]
        ) {
            const sectionElement = sectionRefs.current[activeSection];
            if (sectionElement) {
                // Add a small offset to account for sticky header
                contentRef.current?.scrollTo({
                    top: sectionElement.offsetTop - 100, // Offset to account for header
                    behavior: "smooth",
                });
            }
        }
    }, [activeSection, activeTab]);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    console.log("candidate", candidate);

    return (
        <>
            <div
                className={cn(
                    "fixed inset-y-0 right-0 z-50 bg-white dark:bg-gray-900 border-l transform transition-all duration-300 ease-in-out flex flex-col",
                    isOpen ? "translate-x-0" : "translate-x-full",
                    isFullScreen ? "w-full" : "w-full sm:w-[600px] md:w-[700px] lg:w-1/2"
                )}
            >
                {/* Header with close button */}
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 p-4">
                    <h2 className="text-lg font-semibold">Startup Profile</h2>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleFullScreen}
                            className="h-8 w-8 sm:flex hidden"
                            title={isFullScreen ? "Exit full screen" : "Full screen"}
                        >
                            {isFullScreen ? (
                                <Minimize2 className="h-4 w-4" />
                            ) : (
                                <Maximize2 className="h-4 w-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Main content area */}
                <div className="flex-1 overflow-y-auto" ref={contentRef}>
                    {/* Candidate header with avatar and name */}
                    <div className="p-6 pb-0">
                        <div className="flex items-center space-x-4">
                            <div className="h-16 w-16 rounded-full overflow-hidden">
                                <img
                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold">{candidate.startup_name}</h3>
                            </div>
                        </div>
                    </div>

                    {/* Tab navigation */}
                    <div className="border-b border-gray-200 dark:border-gray-800 mt-6">
                        <div className="flex px-6">
                            <button
                                onClick={() => setActiveTab("profile")}
                                className={cn(
                                    "py-2 px-4 text-sm font-medium relative",
                                    activeTab === "profile"
                                        ? "text-blue-600 dark:text-blue-400"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                )}
                            >
                                Profile
                                {activeTab === "profile" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab("ai-interview")}
                                className={cn(
                                    "py-2 px-4 text-sm font-medium relative",
                                    activeTab === "ai-interview"
                                        ? "text-blue-600 dark:text-blue-400"
                                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                )}
                            >
                                AI Interview
                                {activeTab === "ai-interview" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Tab content */}
                    <div className="p-6">
                        {activeTab === "profile" ? (
                            <div className="space-y-6">

                                {/* Personal Information Section (Combined Personal and Summary) */}
                                <div
                                    ref={(el) => { sectionRefs.current.personal = el; }}
                                    id="personal-section"
                                    className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900"
                                >
                                    <h4 className="font-medium text-base mb-4">
                                        Personal Information
                                    </h4>

                                    <div className="space-y-4 mb-6">
                                        <div className="flex items-center text-sm">
                                            <Mail className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />

                                            <span>{candidate.email || "No Email"}</span>
                                        </div>
                                    </div>

                                    <h4 className="font-medium text-base mb-4">
                                        Startup Summary
                                    </h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {candidate.startup_summary_long || "No summary available."}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col space-y-6 mb-6">
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                                            <CheckSquareIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                            Interview Status
                                        </h4>
                                        {<span className={`px-2 py-1 ${getStatusBadgeClass(candidate.interview_submission_id ? "Complete" : "Pending Interview")} rounded-full text-xs font-medium`}>
                                            {(candidate.interview_submission_id) ? "Completed" : "Pending"}
                                        </span>}
                                    </div>
                                    {candidate.interview_submission_id && (
                                        <>
                                            <p className="text-sm mt-3 text-gray-600 dark:text-gray-400 mb-2">
                                                The startup has completed their AI Interview on{" "}
                                                {parseFormat(candidate.updated_at || "", "h.mmA [on] D MMMM YYYY")}.
                                            </p>
                                            <div
                                                className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                                            >
                                                <ClockIcon className="h-4 w-4 mr-1" />
                                                Duration: {videoDuration ? formatDuration(videoDuration) : (
                                                    <div className="flex items-center">
                                                            <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-blue-600 ml-1" />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {candidate.interview_submission_id && (
                                    <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                            <span className="text-blue-600 dark:text-blue-400 mr-2">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="18"
                                                    height="18"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M3 3v18h18"></path>
                                                    <path d="m19 9-5 5-4-4-3 3"></path>
                                                </svg>
                                            </span>
                                            Interview Summary
                                        </h4>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                                            {(interviewInsights as InterviewInsight)?.investment_memo_summary || "No summary available."}
                                        </p>

                                        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800/50">
                                            <h4 className="font-medium text-base flex items-center mb-4">
                                                <LightbulbIcon className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" />
                                                AI Insights & Recommendations
                                            </h4>
                                            {isLoadingInterviewInsight && (

                                                <div className="flex items-center justify-center h-32">
                                                    <svg
                                                        className="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            strokeWidth="4"
                                                            stroke="currentColor"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4.93 4.93a10 10 0 0114.14 14.14L12 12z"
                                                        ></path>
                                                    </svg>
                                                </div>
                                            )}
                                            {interviewInsights && !isLoadingInterviewInsight && (
                                                <div className="space-y-4">
                                                    <div
                                                        className="border border-blue-100 dark:border-blue-800/30 rounded-lg overflow-hidden"
                                                    >
                                                        {/* Category Header - Static (non-clickable) */}
                                                        <div className="bg-blue-100/50 dark:bg-blue-800/20 px-4 py-3">
                                                            <h5 className="font-semibold text-sm text-blue-800 dark:text-blue-200 flex items-center">
                                                                <UsersIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                                <span className="ml-2">Founder and Team</span>
                                                            </h5>
                                                        </div>

                                                        {/* Category Content - Always visible */}
                                                        <div className="p-4 space-y-4">
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Background
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.founder_and_team?.background || "No background information available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Motivation
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.founder_and_team?.motivation || "No motivation information available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Team
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.founder_and_team?.team.map(elem => <div key={elem.name}>• {elem.name} ({elem.role})</div>) || "No background information available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Team Notes
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.founder_and_team?.team_notes || "No team notes available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Market Fit
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.founder_and_team?.founder_market_fit || "No market fit information available."}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="border border-blue-100 dark:border-blue-800/30 rounded-lg overflow-hidden"
                                                    >
                                                        {/* Category Header - Static (non-clickable) */}
                                                        <div className="bg-blue-100/50 dark:bg-blue-800/20 px-4 py-3">
                                                            <h5 className="font-semibold text-sm text-blue-800 dark:text-blue-200 flex items-center">
                                                                <TrendingUpIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                                <span className="ml-2">Stage and Traction</span>
                                                            </h5>
                                                        </div>

                                                        {/* Category Content - Always visible */}
                                                        <div className="p-4 space-y-4">
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Product Status
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.stage_and_traction?.product_status || "No product status information available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Traction
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.stage_and_traction?.traction || "No traction information available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Near Term Goals
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.stage_and_traction?.near_term_goals || "No near term goals information available."}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="border border-blue-100 dark:border-blue-800/30 rounded-lg overflow-hidden"
                                                    >
                                                        {/* Category Header - Static (non-clickable) */}
                                                        <div className="bg-blue-100/50 dark:bg-blue-800/20 px-4 py-3">
                                                            <h5 className="font-semibold text-sm text-blue-800 dark:text-blue-200 flex items-center">
                                                                <DollarSignIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                                <span className="ml-2">Founding and use of capital</span>
                                                            </h5>
                                                        </div>

                                                        {/* Category Content - Always visible */}
                                                        <div className="p-4 space-y-4">
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Previous Funding
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.funding_and_use_of_capital?.previous_funding || "No previous funding information available."}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Current Raise
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    Amount: ${interviewInsights.funding_and_use_of_capital?.current_raise?.amount || 0} | Valuation: ${interviewInsights.funding_and_use_of_capital?.current_raise?.valuation_cap || 0} | Instrument: {interviewInsights.funding_and_use_of_capital?.current_raise?.instrument || "Not mentioned in transcript"}
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="font-medium text-sm text-gray-800 dark:text-gray-200">
                                                                    Use of Funds
                                                                </div>
                                                                <div className="text-sm text-gray-600 dark:text-gray-400 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
                                                                    {interviewInsights.funding_and_use_of_capital?.use_of_funds.map(elem => <div>• {elem}</div>) || "No use of funds information available."}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {
                                    candidate.interview_submission_id && !isLoadingInterviewInsight && (interviewInsights?.key_questions_and_answers || []).length ? (
                                        <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                                            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                                                <MessageSquareIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                                Key Questions & Responses
                                            </h4>
                                            <div className="space-y-4">
                                                {(interviewInsights?.key_questions_and_answers || []).map((elem, index) => {
                                                    return (
                                                        <div key={index}>
                                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                                                                Q: {elem.question}
                                                            </p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                                                                {elem.answer}
                                                            </p>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    ) : null
                                }
                            </div>
                        )}

                        <InterviewPlayer
                            isLoadingVideo={isLoadingVideo}
                            interviewStatus={interviewStatus}
                            s3VideoLink={candidate.interview_submission_id ? videoInterview?.s3_link || "" : null}
                            videoInterview={candidate.interview_submission_id ? videoInterview as InterviewVideo : {} as InterviewVideo}
                            setVideoDuration={(newDuration: number) => candidate.interview_submission_id ? setVideoDuration(newDuration) : () => console.log("video not loaded")}
                            isHide={activeTab !== "ai-interview"}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
